/**
 * Password rules (Settings > Security, reset page, temporary passwords). Checked in the browser for instant
 * feedback and again on the server. Supabase Auth's own minimum length still applies on top.
 */
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 72; // bcrypt limit used by Supabase Auth

const COMMON = ['password', 'passw0rd', 'qwerty', 'letmein', 'welcome', 'tradeoutlet', 'trade outlet', '123456', 'abc123', 'iloveyou', 'admin', 'changeme'];

export interface PasswordContext { email?: string | null; name?: string | null; current?: string | null }

/** Problems with a new password; empty means it is acceptable. */
export function passwordProblems(pw: string, ctx: PasswordContext = {}): string[] {
  const out: string[] = [];
  if (pw.length < PASSWORD_MIN) out.push(`At least ${PASSWORD_MIN} characters.`);
  if (pw.length > PASSWORD_MAX) out.push(`At most ${PASSWORD_MAX} characters.`);
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  if (classes < 3) out.push('Use at least three of: lower-case, upper-case, numbers, symbols.');
  if (/(.)\1{3,}/.test(pw)) out.push('No character repeated four or more times in a row.');
  const low = pw.toLowerCase();
  if (COMMON.some(c => low.includes(c))) out.push('Avoid common words like "password" or the company name.');
  const local = ctx.email?.split('@')[0]?.toLowerCase();
  if (local && local.length >= 3 && low.includes(local)) out.push('Don\'t include your email address.');
  for (const part of (ctx.name ?? '').toLowerCase().split(/\s+/)) if (part.length >= 3 && low.includes(part)) { out.push('Don\'t include your name.'); break; }
  if (ctx.current && pw === ctx.current) out.push('Choose a password you haven\'t just used.');
  return out;
}

/** 0-4 meter for the UI. */
export function passwordScore(pw: string): number {
  if (!pw) return 0;
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  let s = 0;
  if (pw.length >= PASSWORD_MIN) s++;
  if (pw.length >= 14) s++;
  if (classes >= 3) s++;
  if (classes === 4 || pw.length >= 20) s++;
  return passwordProblems(pw).length ? Math.min(s, 1) : Math.max(s, 2);
}

/** Readable temporary password, e.g. "Brisk-Otter-4821!" (meets the rules). */
export function generateTemporaryPassword(rand: (n: number) => number = n => Math.floor(Math.random() * n)): string {
  const words = ['Amber', 'Brisk', 'Cedar', 'Delta', 'Ember', 'Fable', 'Grove', 'Harbor', 'Indigo', 'Jasper', 'Kestrel', 'Lumen', 'Maple', 'Nimbus', 'Orbit', 'Pine', 'Quartz', 'Raven', 'Summit', 'Tundra', 'Umber', 'Vista', 'Willow', 'Yarrow'];
  const syms = '!#%+?';
  const w = () => words[rand(words.length)];
  // Re-roll numbers like 7777, which break the "no character four times in a row" rule.
  let n = String(1000 + rand(9000));
  while (/(.)\1{3}/.test(n)) n = String(1000 + rand(9000));
  return `${w()}-${w()}-${n}${syms[rand(syms.length)]}`;
}
